import { motion } from "framer-motion";

export const Reveal = ({ children, delay = 0, y = 24, className = "" }) => (
  <motion.div
    initial={{ opacity: 0, y }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-60px" }}
    transition={{ duration: 0.7, delay, ease: [0.22, 1, 0.36, 1] }}
    className={className}
  >
    {children}
  </motion.div>
);

export const StatusDot = ({ tone = "green", className = "" }) => {
  const color = tone === "green" ? "bg-pulse" : tone === "gold" ? "bg-brass" : "bg-fog-600";
  return (
    <span className={`relative inline-flex h-1.5 w-1.5 ${className}`}>
      <span className={`absolute inline-flex h-full w-full rounded-full ${color} opacity-60 animate-ping`} />
      <span className={`relative inline-flex h-1.5 w-1.5 rounded-full ${color}`} />
    </span>
  );
};

const CHIP_TONES = {
  green: "text-pulse-soft border-pulse/25 bg-pulse/5",
  gold: "text-brass-bright border-brass/25 bg-brass/5",
  muted: "text-fog-400 border-white/10 bg-white/[0.03]",
};

export const Chip = ({ children, tone = "muted", className = "", ...rest }) => (
  <span
    className={`inline-flex items-center gap-1.5 border px-2 py-0.5 font-mono text-[10px] uppercase tracking-[0.18em] ${CHIP_TONES[tone]} ${className}`}
    {...rest}
  >
    {children}
  </span>
);

export const Label = ({ children, tone = "muted", className = "" }) => (
  <div
    className={`font-mono text-[10px] uppercase tracking-[0.22em] ${
      tone === "green" ? "text-pulse" : tone === "gold" ? "text-brass" : "text-fog-600"
    } ${className}`}
  >
    {children}
  </div>
);

export const MetaRow = ({ k, v, mono = true, accent = false }) => (
  <div className="flex items-baseline justify-between gap-4 border-b border-white/[0.05] py-2 last:border-0">
    <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-fog-600">{k}</span>
    <span className={`${mono ? "font-mono" : "font-body"} text-xs ${accent ? "text-pulse-soft" : "text-fog-100"}`}>{v}</span>
  </div>
);

export const SectionHeader = ({ eyebrow, title, desc, align = "left" }) => (
  <Reveal className={`mb-12 md:mb-16 ${align === "center" ? "text-center" : ""}`}>
    <div className={`mb-4 flex items-center gap-3 ${align === "center" ? "justify-center" : ""}`}>
      <span className="h-px w-8 bg-pulse/50" />
      <Label tone="green">{eyebrow}</Label>
    </div>
    <h2 className="font-display text-2xl font-bold tracking-tight text-fog-100 sm:text-3xl lg:text-4xl">{title}</h2>
    {desc && <p className={`mt-4 max-w-2xl text-sm leading-relaxed text-fog-400 sm:text-base ${align === "center" ? "mx-auto" : ""}`}>{desc}</p>}
  </Reveal>
);

export const Panel = ({ children, className = "", ...rest }) => (
  <div className={`border border-white/[0.08] bg-panel ${className}`} {...rest}>
    {children}
  </div>
);

export const PanelHeader = ({ children, right }) => (
  <div className="flex items-center justify-between border-b border-white/[0.08] px-4 py-2.5">
    <Label>{children}</Label>
    {right}
  </div>
);

export const ComingSoonBox = ({ label = "STATUS", className = "" }) => (
  <div className={`flex items-center justify-between border border-dashed border-brass/25 bg-brass/[0.03] px-3 py-2 ${className}`}>
    <Label tone="gold">{label}</Label>
    <span className="font-mono text-[11px] tracking-[0.18em] text-brass-bright">COMING SOON</span>
  </div>
);

const MARQUEE_ITEMS = ["AUTONOMOUS", "PROGRAMMABLE", "COORDINATED", "PERMISSIONED", "COMPOSABLE", "INTELLIGENT", "ONCHAIN"];

export const Marquee = ({ className = "" }) => (
  <div className={`overflow-hidden border-y border-white/[0.06] bg-ink-850 ${className}`} data-testid="brand-marquee">
    <div className="animate-marquee flex w-max items-center py-3.5">
      {[0, 1].map((half) => (
        <div key={half} className="flex items-center" aria-hidden={half === 1}>
          {MARQUEE_ITEMS.map((w) => (
            <span key={`${half}-${w}`} className="flex items-center">
              <span className="px-8 font-display text-xs font-semibold uppercase tracking-[0.35em] text-fog-600">{w}</span>
              <span className="h-1 w-1 rotate-45 bg-brass/60" />
            </span>
          ))}
        </div>
      ))}
    </div>
  </div>
);
